/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/solidjs-ark
 * @description Ark-native SolidJS status badge for the adapter UI variant.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { classNames } from "../lib/classNames";

const STATUS_LABELS: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

const STATUS_CLASSES: Record<AdapterStatusType, string> = {
  connected: "border-emerald-500/30 bg-emerald-50 text-emerald-950",
  disconnected: "border-slate-200 bg-slate-100 text-slate-600",
  error: "border-red-500/30 bg-red-50 text-red-950",
  initializing: "border-amber-500/40 bg-amber-50 text-amber-950",
};

const DOT_CLASSES: Record<AdapterStatusType, string> = {
  connected: "bg-emerald-500",
  disconnected: "bg-slate-400",
  error: "bg-red-500",
  initializing: "bg-amber-500 animate-pulse motion-reduce:animate-none",
};

const SIZE_CLASSES = {
  sm: "px-2.5 py-0.5 text-[0.625rem]",
  md: "px-3 py-1 text-xs",
} as const;

const DOT_SIZE_CLASSES = {
  sm: "h-1.5 w-1.5",
  md: "h-2 w-2",
} as const;

/**
 * Props for rendering the Ark UI Solid adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property size Visual size variant for the badge and status dot.
 * @property class Optional class name merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  class?: string;
}

/** Renders a compact Ark-flavoured badge for adapter lifecycle state. */
export function AdapterStatusBadge(
  props: AdapterStatusBadgeProps,
): JSX.Element {
  const size = () => props.size ?? "sm";

  return (
    <span
      class={classNames(
        "inline-flex items-center gap-1.5 rounded-full border font-semibold uppercase transition-colors",
        STATUS_CLASSES[props.status],
        SIZE_CLASSES[size()],
        props.class,
      )}
      data-gn-adapters-ark-component="status-badge"
      data-status={props.status}
    >
      <span
        aria-hidden="true"
        class={classNames(
          "rounded-full",
          DOT_CLASSES[props.status],
          DOT_SIZE_CLASSES[size()],
        )}
      />
      {props.showLabel !== false && <span>{STATUS_LABELS[props.status]}</span>}
    </span>
  );
}
